/**
 * AppBreadcrumb - Site-wide breadcrumb navigation component
 *
 * Renders breadcrumb trail based on current route hierarchy.
 * Integrates with focus mode - hidden when focus mode is active.
 *
 * Placement: First child of SidebarInset in AppLayout
 * Positioning: sticky top-0 z-30 within scroll container
 *
 * Task: 81.3 - Create AppBreadcrumb component
 */

import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbEllipsis,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { useBreadcrumbs } from '@/providers/breadcrumb-provider';
import { useFocusModeOptional } from '@/providers/focus-mode-provider';
import { cn } from '@/utils/cn';

// ──────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────

interface AppBreadcrumbProps {
  /** Optional class name for container */
  className?: string;
}

// ──────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────

/**
 * AppBreadcrumb - Site-wide breadcrumb navigation component
 *
 * Features:
 * - Renders breadcrumb trail from route hierarchy via useBreadcrumbs()
 * - Sticky positioning at top of scroll container
 * - Integrates with focus mode (hides when active)
 * - Supports icons on first segment (typically Home)
 * - Uses shadcn/ui breadcrumb primitives
 *
 * @example
 * ```tsx
 * // In AppLayout
 * <SidebarInset>
 *   <AppBreadcrumb />
 *   <div className="py-6">
 *     <Outlet />
 *   </div>
 * </SidebarInset>
 * ```
 */
export function AppBreadcrumb({ className }: AppBreadcrumbProps) {
  const breadcrumbs = useBreadcrumbs();
  const focusModeContext = useFocusModeOptional();
  const isFocusMode = focusModeContext?.isFocusMode ?? false;

  // Don't render if no breadcrumbs or only home/single segment
  if (breadcrumbs.length <= 1) {
    return null;
  }

  return (
    <div
      className={cn(
        'sticky top-0 z-30 border-b border-border bg-background',
        'transition-all duration-300',
        isFocusMode && 'h-0 overflow-hidden border-b-0 opacity-0',
        className
      )}
      aria-hidden={isFocusMode}
    >
      <div className="container-max px-4 py-3 md:px-6 lg:px-8">
        <Breadcrumb>
          <BreadcrumbList className="flex-nowrap">
            {breadcrumbs.map((item, index) => {
              const isFirst = index === 0;
              const isLast = index === breadcrumbs.length - 1;
              const isMiddle = !isFirst && !isLast;

              // On mobile, collapse middle items for deep paths (4+ items)
              // Show: first > ... > last
              if (isMiddle && breadcrumbs.length >= 4) {
                // Only render the ellipsis once (at index 1)
                if (index === 1) {
                  return (
                    <Fragment key="ellipsis">
                      <BreadcrumbSeparator />
                      <BreadcrumbItem className="sm:hidden">
                        <BreadcrumbEllipsis />
                      </BreadcrumbItem>
                      {/* Show full path on sm+ screens */}
                      <BreadcrumbItem className="hidden sm:inline-flex">
                        <BreadcrumbLink asChild>
                          <Link to={item.path}>{item.label}</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                    </Fragment>
                  );
                }
                // Hide other middle items on mobile, show on sm+
                return (
                  <Fragment key={item.path}>
                    <BreadcrumbSeparator className="hidden sm:inline-flex" />
                    <BreadcrumbItem className="hidden sm:inline-flex">
                      <BreadcrumbLink asChild>
                        <Link to={item.path}>{item.label}</Link>
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                  </Fragment>
                );
              }

              return (
                <Fragment key={item.path}>
                  {index > 0 && <BreadcrumbSeparator />}
                  <BreadcrumbItem className={isLast ? 'min-w-0' : undefined}>
                    {item.isCurrent ? (
                      <BreadcrumbPage
                        className="max-w-[180px] truncate sm:max-w-none"
                        title={item.label}
                      >
                        {item.label}
                      </BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink asChild>
                        <Link to={item.path} className="inline-flex items-center">
                          {item.icon && isFirst && (
                            <item.icon className="mr-1.5 size-4" />
                          )}
                          {item.label}
                        </Link>
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                </Fragment>
              );
            })}
          </BreadcrumbList>
        </Breadcrumb>
      </div>
    </div>
  );
}
